import React from 'react';
import { TRENDING_OFFERS, UPCOMING_OFFERS, MAIN_COUPON_CODE, ALL_COUPONS } from '../constants';
import { Gift, Zap, Check, ArrowRight, Clock, Star, Flame, Trophy, Copy, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

const EduOffers: React.FC = () => {
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000 perspective-2000 pb-12 sm:pb-32">
      {/* Header Section */}
      <section className="relative pt-12 pb-12 sm:pt-32 sm:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-morphism border border-yellow-600/30 text-yellow-400 text-[8px] sm:text-[10px] font-black tracking-[0.3em] uppercase animate-float">
              <Gift size={12} className="animate-pulse" />
              2026 Enrollment Vault
            </div>
          </div>
          
          <h1 className="text-4xl sm:text-7xl md:text-9xl font-black mb-6 italic uppercase tracking-tighter leading-[1.1] text-white">
            PW <span className="text-blue-500 not-italic">LIVE</span> <br /> OFFERS
          </h1>
          <p className="text-sm sm:text-xl text-gray-400 max-w-2xl mx-auto font-medium mb-10 leading-relaxed">
            Our platform provides trending batches and <span className="text-white font-bold italic">Vishwas Diwas</span> festive sales directly from verified official sources.
          </p>
          
          <div className="flex justify-center gap-6 opacity-40 grayscale">
             <Trophy size={28} className="text-blue-500" />
             <Star size={28} className="text-yellow-500" />
             <Zap size={28} className="text-blue-400" />
          </div>
        </div>
      </section>

      {/* Trending Offers Section */}
      <section className="max-w-7xl mx-auto px-4 mb-16 sm:mb-32">
        <div className="flex items-center gap-4 mb-8 sm:mb-16 border-l-4 border-blue-600 pl-4 sm:pl-8">
           <Flame className="text-orange-500 animate-bounce" size={24} />
           <div>
              <h2 className="text-2xl sm:text-4xl font-black text-white uppercase tracking-tighter italic">Trending Now</h2>
              <p className="text-gray-500 font-bold uppercase text-[8px] sm:text-[10px] tracking-widest italic">Official Selection</p>
           </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
          {TRENDING_OFFERS.map((offer) => (
            <div key={offer.id} className="card-3d bg-[#111] border border-white/5 rounded-[30px] sm:rounded-[40px] overflow-hidden group hover:border-blue-500/40 transition-all shadow-2xl relative">
              <div className="aspect-[16/9] relative overflow-hidden">
                <img src={offer.image} alt={offer.title} loading="lazy" className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 transition-all duration-1000" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#111] to-transparent" />
                <div className="absolute top-4 right-4">
                  <span className="bg-blue-600 text-white text-[8px] sm:text-[10px] font-black uppercase px-3 py-1 rounded-full flex items-center gap-1 shadow-2xl shadow-blue-600/30">
                    <ShieldCheck size={10} /> Verified
                  </span>
                </div>
                <div className="absolute bottom-4 left-6 sm:bottom-6 sm:left-8">
                   <h3 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tighter italic">{offer.title}</h3>
                </div>
              </div>
              <div className="p-8 sm:p-10">
                <p className="text-blue-400 font-black uppercase tracking-widest text-[9px] sm:text-xs mb-4">{offer.subtitle}</p>
                <div className="flex items-baseline gap-3 mb-6">
                   <span className="text-2xl sm:text-4xl font-black text-yellow-500 tracking-tighter italic">{offer.discount}</span>
                   {offer.originalPrice && <span className="text-gray-600 line-through text-base font-bold italic">{offer.originalPrice}</span>}
                </div>
                <ul className="space-y-3 mb-8">
                  {offer.features.map((feat, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-xs sm:text-sm text-gray-400 font-medium">
                      <CheckCircle2 size={14} className="text-green-500 shrink-0" /> {feat}
                    </li>
                  ))}
                </ul>
                <Link to={offer.link} className="w-full inline-flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white py-4 sm:py-5 rounded-2xl font-black uppercase text-[10px] sm:text-xs tracking-widest transition-all active:scale-95 shadow-xl group">
                  Redeem Offer <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Universal Code Reward Section */}
      <section className="max-w-5xl mx-auto px-4 mb-16 sm:mb-32">
        <div className="bg-gradient-to-br from-blue-600 to-blue-900 rounded-[40px] sm:rounded-[60px] p-10 sm:p-24 text-center relative overflow-hidden group">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20 pointer-events-none"></div>
          <div className="relative z-10 flex flex-col items-center">
            <h2 className="text-3xl sm:text-7xl font-black text-white uppercase tracking-tighter mb-6 italic leading-tight">
              REDEEM YOUR <br /> UNIVERSAL VOUCHER
            </h2>
            <p className="text-white/70 font-bold text-sm sm:text-lg max-w-xl mx-auto mb-10 uppercase">
              One code for all. Use this official ambassador coupon for any batch on this page.
            </p>
            
            <div className="w-full max-w-sm bg-black/40 backdrop-blur-2xl rounded-[30px] sm:rounded-[40px] p-8 sm:p-12 border border-white/10 shadow-3xl mb-8 transform-gpu hover:scale-105 transition-all">
               <p className="text-[8px] text-white/30 font-black uppercase tracking-[0.3em] mb-4">LIVE AMBASSADOR CODE</p>
               <h3 className="text-4xl sm:text-7xl font-mono font-black text-blue-400 tracking-tighter mb-8">{MAIN_COUPON_CODE}</h3>
               <button 
                  onClick={() => handleCopy(MAIN_COUPON_CODE)}
                  className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest text-[10px] sm:text-xs transition-all active:scale-95 flex items-center justify-center gap-3 ${copiedCode === MAIN_COUPON_CODE ? 'bg-green-500 text-white' : 'bg-white text-blue-900 hover:bg-gray-100'}`}
               >
                  {copiedCode === MAIN_COUPON_CODE ? <CheckCircle2 size={18} /> : <Copy size={18} />}
                  {copiedCode === MAIN_COUPON_CODE ? 'COPIED' : 'COPY VOUCHER'}
               </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EduOffers;